import type { WeatherLayerUnion } from "@/layers";
import { isBinaryAnomalyLayer } from "@/layers/png/binary/TemperatureAnomalyPoc";
import type { ActiveLayers } from "@/overlay/components/PopupPlotWindow";
import { getAllEnsSelect } from "@/utility/ensemble";
import { highchartsColors } from "@/utility/highchartsValues";
import { useMemo } from "react";
import { useMaps } from "../MapsState";
import { EXCLUDED_LAYERS } from "./constants";

import type { LayerKind } from "@/layers/utility/createLayerObject";
import { getModel } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/useModel";
import { DefaultPlotSeries, StartColor } from "@/reducer/client-models";
import Logger from "@mm/logging";

const logger = Logger.fromFilename(__filename);

function readCustomOption(layer: WeatherLayerUnion, key: "init_date" | "api_url_suffix"): string | undefined {
  const value = (layer as { custom_options?: Record<string, unknown> }).custom_options?.[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
}

export const useActiveLayers = (tabId: number) => {
  const maps = useMaps(tabId);

  const layers = useMemo(() => {
    const activeLayerList: ActiveLayers[] = [];
    for (const cartographicMap of maps) {
      if (cartographicMap) {
        // biome-ignore lint/complexity/noForEach: <explanation>
        cartographicMap.layers
          ?.filter(
            (layer) =>
              layer.kind &&
              !isBinaryAnomalyLayer(layer) &&
              !EXCLUDED_LAYERS.includes(layer.kind as LayerKind) &&
              layer.opacity !== 0 &&
              layer.show,
          )
          .forEach((_layer) => {
            const layer = _layer as WeatherLayerUnion;
            if (!layer.kind) {
              logger.error("Layer doesn't have a type label [layer.kind]");
              return;
            }
            const { parameter_unit: parameter, model: layerModel, calibrated } = layer;
            const modelSchema = getModel(layerModel);
            const ensSelect = getAllEnsSelect(
              layer.ens_select ?? undefined,
              modelSchema?.num_ensemble_members ?? undefined,
            );

            if (modelSchema) {
              const seriesColor = highchartsColors
                ? highchartsColors[activeLayerList.length % highchartsColors.length].toString()
                : StartColor;
              const label = layer.label?.trim() || undefined;

              activeLayerList.push({
                mapOrPlotTitle: cartographicMap.title,
                parameter,
                label,
                model: modelSchema,
                plotSerieOptions: {
                  ...DefaultPlotSeries,
                  ens_select: ensSelect,
                  series_color: seriesColor,
                  init_date: readCustomOption(layer, "init_date"),
                  api_url_suffix: readCustomOption(layer, "api_url_suffix"),
                  // TODO move calibrated outside plotSerieOptions, so
                  // we don't need to pass DefaultPlotSeries
                  calibrated: !!calibrated,
                },
              });
            } else {
              console.error("Could not find the modelSchema", layerModel);
            }
          });
      }
    }

    return activeLayerList;
  }, [maps]);

  return {
    layers,
  };
};
